import { FastAverageColor } from 'fast-average-color';

/**
 * 计算颜色的相对亮度（根据 WCAG 标准）
 * @param r 红色值 (0-255)
 * @param g 绿色值 (0-255)
 * @param b 蓝色值 (0-255)
 * @returns 亮度值 (0-1)
 */
const getRelativeLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const val = c / 255;
    return val <= 0.03928 ? val / 12.92 : ((val + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * 调整浅色使其更深，确保文字可读性
 * @param r 红色值 (0-255)
 * @param g 绿色值 (0-255)
 * @param b 蓝色值 (0-255)
 * @param a 透明度 (0-1)
 * @returns 调整后的 RGBA 字符串
 */
const adjustLightColor = (r: number, g: number, b: number, a: number): string => {
  const luminance = getRelativeLuminance(r, g, b);

  // 如果亮度大于 0.5（偏亮），则进行调暗处理
  if (luminance > 0.5) {
    // 将颜色混合黑色，保持色调的同时降低亮度
    // 亮度越高，混合的黑色越多
    const darkenFactor = 0.3 + (luminance - 0.5) * 0.4; // 0.3 到 0.5 的调暗系数
    const newR = Math.round(r * (1 - darkenFactor));
    const newG = Math.round(g * (1 - darkenFactor));
    const newB = Math.round(b * (1 - darkenFactor));

    // 确保透明度足够高以提供良好对比度
    const newA = Math.max(a, 0.8);

    return `rgba(${newR}, ${newG}, ${newB}, ${newA})`;
  }

  // 如果颜色已经够深，保持原样
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/**
 * 获取图片的平均颜色
 * @param src 图片源地址
 * @returns 包含颜色信息的对象
 */
export const getAverageColor = async (src: string) => {
  try {
    const fac = new FastAverageColor();

    // 创建图片元素
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('图片加载失败'));
    });

    const color = await fac.getColor(img);

    // 从 rgba 字符串中提取 RGBA 值
    const rgbaMatch = color.rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*(\d*\.?\d+)?\)/);

    let adjustedColor = color.rgba;
    if (rgbaMatch) {
      const r = Number.parseInt(rgbaMatch[1], 10);
      const g = Number.parseInt(rgbaMatch[2], 10);
      const b = Number.parseInt(rgbaMatch[3], 10);
      const a = rgbaMatch[4] ? Number.parseFloat(rgbaMatch[4]) : 1;

      // 调整浅色
      adjustedColor = adjustLightColor(r, g, b, a);
    }

    return {
      color: adjustedColor,
      originalColor: color.hex,
      rgb: color.rgb,
      rgba: color.rgba,
      value: color.value,
      isDark: color.isDark,
      isLight: color.isLight,
      success: true,
    };
  } catch (error) {
    console.error('获取图片平均颜色失败:', error);
    return {
      color: null,
      success: false,
      error: error instanceof Error ? error.message : '获取颜色失败',
    };
  }
};
